import React, { useState, useEffect } from 'react';
import { StyleSheet, ScrollView, TouchableOpacity, View, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useAuthStore } from '../../stores/authStore';
import { usersApi } from '../../api/users';
import { useLogout } from '../../hooks/useAuth';

export default function AccountDeletionScreen({ navigation }: any) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const logout = useLogout();

  const [confirmStep, setConfirmStep] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state when navigating away from this screen so a stale
  // "Account Deleted" success view isn't shown if the user lands back here
  // (e.g. after SSO causes the RootNavigator to swap conditional screens).
  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', () => {
      setSuccess(false);
      setConfirmStep(false);
      setEmailInput('');
      setError(null);
    });
    return unsubscribe;
  }, [navigation]);

  // Navigate to the Auth screen, replacing the entire stack so this screen
  // is removed from the navigation history. If AccountDeletion stays in the
  // stack, a subsequent auth-state change (e.g. Apple/Google SSO) removes the
  // "Auth" route from RootNavigator's conditional, causing React Navigation
  // to fall back to this screen — showing a stale "Account Deleted" view.
  const goToAuth = () => {
    navigation.reset({ index: 0, routes: [{ name: 'Auth' }] });
  };

  const handleConfirm = async () => {
    if (emailInput.trim().toLowerCase() !== (user?.email || '').toLowerCase()) {
      setError(t('accountDeletion.emailMismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await usersApi.requestAccountDeletion();
      setSuccess(true);
      try { await logout(); } catch {}
      clearAuth();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('accountDeletion.requestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderDataInfo = () => (
    <View style={styles.section}>
      <Text style={[styles.subheading, { color: colors.text }]}>
        {t('accountDeletion.dataTitle')}
      </Text>
      <Text style={[styles.body, { color: colors.textSecondary }]}>
        {t('accountDeletion.dataDeleted')}
      </Text>
      <Text style={[styles.body, { color: colors.textSecondary, marginTop: 8 }]}>
        {t('accountDeletion.subUsersDeleted')}
      </Text>
      <Text style={[styles.body, { color: colors.textSecondary, marginTop: 8 }]}>
        {t('accountDeletion.subUsersRetained')}
      </Text>
      <Text style={[styles.body, { color: colors.textSecondary, marginTop: 8 }]}>
        {t('accountDeletion.dataRetained')}
      </Text>
      <Text style={[styles.body, { color: colors.textSecondary, marginTop: 8 }]}>
        {t('accountDeletion.retentionPeriod')}
      </Text>
    </View>
  );

  if (success) {
    return (
      <ScrollView contentContainerStyle={[styles.container, responsiveContentStyle]}>
        <View style={styles.centerSection}>
          <Text style={[styles.successTitle, { color: colors.text }]}>
            {t('accountDeletion.successTitle')}
          </Text>
          <Text style={[styles.body, { color: colors.textSecondary, textAlign: 'center' }]}>
            {t('accountDeletion.successBody')}
          </Text>
        </View>
        <TouchableOpacity
          onPress={goToAuth}
          style={[styles.button, { backgroundColor: colors.accent, marginTop: 24 }]}
          activeOpacity={0.8}
        >
          <Text style={[styles.buttonText, { color: colors.accentText }]}>
            {t('accountDeletion.goToLogin')}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    );
  }

  if (!isAuthenticated) {
    return (
      <ScrollView contentContainerStyle={[styles.container, responsiveContentStyle]}>
        <Text style={[styles.heading, { color: colors.text }]}>
          {t('accountDeletion.loggedOutIntro')}
        </Text>

        <Text style={[styles.subheading, { color: colors.text }]}>
          {t('accountDeletion.stepsTitle')}
        </Text>
        <Text style={[styles.body, { color: colors.textSecondary }]}>
          {'\n1. '}
          {t('accountDeletion.loggedOutStep1')}
          {'\n\n2. '}
          {t('accountDeletion.loggedOutStep2')}
          {'\n\n3. '}
          {t('accountDeletion.loggedOutStep3')}
        </Text>

        {renderDataInfo()}

        <TouchableOpacity
          onPress={goToAuth}
          style={[styles.button, { backgroundColor: colors.accent }]}
          activeOpacity={0.8}
        >
          <Text style={[styles.buttonText, { color: colors.accentText }]}>
            {t('accountDeletion.signInButton')}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <ScrollView
        contentContainerStyle={[styles.container, responsiveContentStyle]}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={[styles.heading, { color: colors.text }]}>
          {t('accountDeletion.loggedInIntro')}
        </Text>

      <View style={[styles.warningBox, { backgroundColor: colors.error + '15', borderColor: colors.error + '40' }]}>
        <Text style={[styles.warningBody, { color: colors.error }]}>
          {t('accountDeletion.planLossWarning')}
        </Text>
      </View>

      {renderDataInfo()}

      {!confirmStep ? (
        <>
          <Text style={[styles.subheading, { color: colors.text }]}>
            {t('accountDeletion.stepsTitle')}
          </Text>
          <Text style={[styles.body, { color: colors.textSecondary }]}>
            {'\n1. '}
            {t('accountDeletion.loggedInStep1')}
            {'\n\n2. '}
            {t('accountDeletion.loggedInStep2')}
          </Text>

          <TouchableOpacity
            onPress={() => setConfirmStep(true)}
            style={[styles.button, { backgroundColor: colors.error, marginTop: 16 }]}
            activeOpacity={0.8}
          >
            <Text style={[styles.buttonText, { color: '#fff' }]}>
              {t('accountDeletion.requestButton')}
            </Text>
          </TouchableOpacity>
        </>
      ) : (
        <>
          <Text style={[styles.warningText, { color: colors.text }]}>
            {t('accountDeletion.confirmWarning')}
          </Text>

          {user?.email && (
            <View style={[styles.emailDisplay, { backgroundColor: colors.card, borderColor: colors.border }] }>
              <Text style={[styles.emailLabel, { color: colors.textSecondary }]}>
                {t('accountDeletion.yourEmail')}
              </Text>
              <Text style={[styles.emailValue, { color: colors.text }]}>
                {user.email}
              </Text>
            </View>
          )}

          <TextInput
            label={t('accountDeletion.emailConfirmLabel')}
            value={emailInput}
            onChangeText={setEmailInput}
            mode="outlined"
            autoCapitalize="none"
            keyboardType="email-address"
            style={styles.input}
            textColor={colors.text}
            theme={{ colors: { primary: colors.accent } }}
          />

          {error && (
            <Text style={[styles.errorText, { color: colors.error }]}>
              {error}
            </Text>
          )}

          <TouchableOpacity
            onPress={handleConfirm}
            disabled={submitting || !emailInput.trim()}
            style={[styles.button, { backgroundColor: colors.error, opacity: submitting || !emailInput.trim() ? 0.5 : 1 }]}
            activeOpacity={0.8}
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={[styles.buttonText, { color: '#fff' }]}>
                {t('accountDeletion.confirmButton')}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => { setConfirmStep(false); setEmailInput(''); setError(null); }}
            style={styles.backButton}
            activeOpacity={0.7}
          >
            <Text style={[styles.linkText, { color: colors.textSecondary }]}>
              {t('accountDeletion.cancel')}
            </Text>
          </TouchableOpacity>
        </>
      )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  container: {
    padding: 20,
    paddingBottom: 40,
  },
  section: {
    marginBottom: 24,
  },
  centerSection: {
    alignItems: 'center',
    marginTop: 40,
  },
  heading: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 20,
  },
  subheading: {
    fontSize: 16,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    lineHeight: 21,
  },
  warningText: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 22,
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
  },
  input: {
    marginBottom: 16,
  },
  errorText: {
    fontSize: 13,
    marginBottom: 12,
  },
  button: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 16,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  backButton: {
    alignItems: 'center',
    paddingBottom: 16,
  },
  linkText: {
    fontSize: 14,
  },
  warningBox: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 20,
  },
  warningBody: {
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '600',
  },
  emailDisplay: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
  },
  emailLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  emailValue: {
    fontSize: 15,
    fontWeight: '600',
    fontFamily: 'monospace',
  },
});
